// Considered: the four existing inspector siblings (ContextLevelInspectorOrganism,
// ContextRootInspectorMolecule, ApplicabilityInspectorMolecule, LevelFilterPopoverOrganism) —
// rejected because every one is bound to the Context authoring domain (levels/roots/filters); none
// resolves an arbitrary A2UI `SelectedNode` against the component catalog + composition registry.
// This is a new, generic node inspector (spec Reuse Audit, EAINT-844 Phase 5).
import React from 'react';
import { useTranslation } from 'localization';
import { normalizeCompositionSlots } from '@center-platform/space-format/bindings';
import type { SpaceSettings } from 'types/space';
import type { SelectedNode } from '../../../composition-host/types';
import type { ComposerOverride } from '../../../composer/composerStore';
import type { GridPosition } from '../../../composer/gridPlacement';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import InputAtom from '../../atoms/Input/InputAtom';
import TextareaAtom from '../../atoms/Textarea/TextareaAtom';
import CheckboxAtom from '../../atoms/checkbox/Checkbox';
import RebasePolicyControlMolecule, { RebasePolicy } from './RebasePolicyControlMolecule';
import './ComposerInspectorMolecule.scss';

const DEFAULT_GRID_POSITION: GridPosition = { x: 0, y: 0, width: 4, height: 4 };

/** One selectable context or view for the override lane's dropdowns. */
export interface OverrideOption {
  systemName: string;
  label: string;
}

export interface ComposerInspectorMoleculeProps {
  selectedNode: SelectedNode | null;
  /**
   * The selected component's session-only context/view override, if any (SC4). Undefined
   * (distinct from an empty object) means no override lane renders at all — the caller has no
   * anchor sphere yet or lacks `edit`.
   */
  sessionOverride?: ComposerOverride;
  /** Contexts available on the anchor sphere — the override lane's `context` options. */
  overrideContexts?: OverrideOption[];
  /** Views for `sessionOverride.contextSystemName` — the override lane's `view` options. */
  overrideViews?: OverrideOption[];
  overrideViewsLoading?: boolean;
  onOverrideContextChange?: (componentId: string, contextSystemName: string | undefined) => void;
  onOverrideViewChange?: (componentId: string, viewSystemName: string | undefined) => void;
  onApplyOverrideToDocument?: (componentId: string) => void;
  onClearOverride?: (componentId: string) => void;
  /**
   * Opens a composition-backed node in the Explorer's playground (its own docs/knobs/emit-log
   * view) — absent for code-backed nodes, which have no registry entry to open. `Fork to edit`
   * from the source mockup is deliberately not offered: there is no composition-target composer
   * shell yet (OQ-8), so a fork would land nowhere.
   */
  onOpenInPlayground?: (systemName: string) => void;
  /** Precise numeric x/y/w/h editing for the selected component's grid placement (EAINT-844 Phase F5a). Absent when the caller lacks `edit` or the target has no grid-laid-out surface. */
  onSetGridPosition?: (componentId: string, gridPosition: GridPosition) => void;
  /**
   * The selected node's currently resolved composition version (from the artifact's own
   * lockfile) — feeds `RebasePolicyControlMolecule`'s pinned write. Absent when nothing has
   * resolved yet, which disables the pinned option.
   */
  rebasePolicyResolvedVersion?: number;
  /** Writes the rebase policy (and, when pinning, `componentVersion`) for the selected node's composition ref. Absent hides the control — e.g. the caller lacks `edit`, or the node has no `compositionRef`. */
  onChangeRebasePolicy?: (componentId: string, policy: RebasePolicy, componentVersion: number | undefined) => void;
  /**
   * The Space-settings panel (EAINT-844 Phase F5b), shown when `selectedNode` is null — name/
   * description (the metaspace's header fields, not part of the document), and `doc.settings`
   * (toolbar enabled, item toggles, default item, theme, columns). Absent `spaceSettings` renders
   * nothing (e.g. a locked catalog preview with no real Space behind it).
   */
  spaceName?: string;
  spaceDescription?: string;
  spaceSettings?: SpaceSettings;
  onNameChange?: (name: string) => void;
  onDescriptionChange?: (description: string) => void;
  onSettingsChange?: (patch: Partial<SpaceSettings>) => void;
}

/**
 * The selected-node half of the composer's side panel (props/document-binding editing lands in a
 * later phase — this slice surfaces identity + registry origin so selection is visibly wired end
 * to end, SC2). Distinguishes a code-backed catalog entry from a composition ref, since the two
 * have different downstream editing surfaces (props schema vs. interface panel).
 *
 * Also owns the session-override lane (SC4): a component's context/view override lives only in
 * `ComposerState.overrides` until `applyOverrideToDocument` promotes it — selecting a context or
 * view here calls straight through to `composerStore`'s session-override ops, never the draft
 * edit reducer. The document-binding lane (read-only display of the component's persisted
 * context/view) is a separate, later slice.
 */
const ComposerInspectorMolecule: React.FC<ComposerInspectorMoleculeProps> = ({
  selectedNode,
  sessionOverride,
  overrideContexts = [],
  overrideViews = [],
  overrideViewsLoading = false,
  onOverrideContextChange,
  onOverrideViewChange,
  onApplyOverrideToDocument,
  onClearOverride,
  onOpenInPlayground,
  onSetGridPosition,
  rebasePolicyResolvedVersion,
  onChangeRebasePolicy,
  spaceName,
  spaceDescription,
  spaceSettings,
  onNameChange,
  onDescriptionChange,
  onSettingsChange,
}) => {
  const { t } = useTranslation();

  if (!selectedNode) {
    return (
      <div className="composer-inspector composer-inspector--space-settings" data-testid="composer-inspector-space-settings">
        <span className="composer-inspector__section-heading">{t('composer.inspector.space.heading')}</span>
        <div className="composer-inspector__field">
          <label htmlFor="composer-inspector-space-name">{t('composer.inspector.space.nameLabel')}</label>
          <InputAtom
            id="composer-inspector-space-name"
            value={spaceName ?? ''}
            disabled={!onNameChange}
            onChange={(e) => onNameChange?.(e.target.value)}
          />
        </div>
        <div className="composer-inspector__field">
          <label htmlFor="composer-inspector-space-description">{t('composer.inspector.space.descriptionLabel')}</label>
          <TextareaAtom
            id="composer-inspector-space-description"
            value={spaceDescription ?? ''}
            disabled={!onDescriptionChange}
            onChange={(e) => onDescriptionChange?.(e.target.value)}
            rows={3}
          />
        </div>
        <div className="composer-inspector__field composer-inspector__field--inline">
          <CheckboxAtom
            label={t('composer.inspector.space.toolbarEnabledLabel')}
            checked={spaceSettings?.showMetasphereToolbar !== false}
            onChange={(checked) => onSettingsChange?.({ showMetasphereToolbar: checked })}
            className="composer-inspector__checkbox"
          />
        </div>
        <div className="composer-inspector__field">
          <label htmlFor="composer-inspector-space-enabled-items">{t('composer.inspector.space.enabledItemsLabel')}</label>
          <InputAtom
            id="composer-inspector-space-enabled-items"
            value={(spaceSettings?.enabledToolbarItems ?? []).join(', ')}
            disabled={!onSettingsChange}
            placeholder={t('composer.inspector.space.itemsPlaceholder')}
            onChange={(e) =>
              onSettingsChange?.({
                enabledToolbarItems: e.target.value
                  .split(',')
                  .map((v) => v.trim())
                  .filter(Boolean),
              })
            }
          />
        </div>
        <div className="composer-inspector__field">
          <label htmlFor="composer-inspector-space-disabled-items">{t('composer.inspector.space.disabledItemsLabel')}</label>
          <InputAtom
            id="composer-inspector-space-disabled-items"
            value={(spaceSettings?.disabledToolbarItems ?? []).join(', ')}
            disabled={!onSettingsChange}
            placeholder={t('composer.inspector.space.itemsPlaceholder')}
            onChange={(e) =>
              onSettingsChange?.({
                disabledToolbarItems: e.target.value
                  .split(',')
                  .map((v) => v.trim())
                  .filter(Boolean),
              })
            }
          />
        </div>
        <div className="composer-inspector__field">
          <label htmlFor="composer-inspector-space-default-item">{t('composer.inspector.space.defaultItemLabel')}</label>
          <InputAtom
            id="composer-inspector-space-default-item"
            value={spaceSettings?.toolbarDefaultActiveItem ?? ''}
            disabled={!onSettingsChange}
            onChange={(e) => onSettingsChange?.({ toolbarDefaultActiveItem: e.target.value || undefined })}
          />
        </div>
        <div className="composer-inspector__field">
          <label htmlFor="composer-inspector-space-theme">{t('composer.inspector.space.themeLabel')}</label>
          <DropdownAtom
            inputId="composer-inspector-space-theme"
            value={spaceSettings?.theme ?? null}
            options={[
              { label: t('composer.inspector.space.themeLight'), value: 'light' },
              { label: t('composer.inspector.space.themeDark'), value: 'dark' },
            ]}
            optionLabel="label"
            optionValue="value"
            showClear
            disabled={!onSettingsChange}
            onChange={(e) => onSettingsChange?.({ theme: e.value ?? undefined })}
          />
        </div>
        <div className="composer-inspector__field">
          <label htmlFor="composer-inspector-space-columns">{t('composer.inspector.space.columnsLabel')}</label>
          <InputAtom
            id="composer-inspector-space-columns"
            type="number"
            value={spaceSettings?.columns != null ? String(spaceSettings.columns) : ''}
            disabled={!onSettingsChange}
            onChange={(e) => {
              const parsed = Number(e.target.value);
              onSettingsChange?.({ columns: Number.isFinite(parsed) && e.target.value !== '' ? parsed : undefined });
            }}
          />
        </div>
      </div>
    );
  }

  const originLabel = selectedNode.compositionRef
    ? t('composer.inspector.originComposition')
    : t('composer.inspector.originCatalog');

  const showOverrideLane = sessionOverride !== undefined;
  const hasOverride = Boolean(sessionOverride?.contextSystemName || sessionOverride?.viewSystemName);
  const contextOptions = overrideContexts.map((c) => ({ label: c.label, value: c.systemName }));
  const viewOptions = overrideViews.map((v) => ({ label: v.label, value: v.systemName }));
  const documentContext = selectedNode.contextViewBinding?.contextSystemName;
  const compositionInterface = selectedNode.compositionInterface;
  const emits = compositionInterface?.emits ?? [];
  const slots = normalizeCompositionSlots(compositionInterface?.slots ?? []);
  const propEntries = compositionInterface ? Object.entries(compositionInterface.props) : [];
  const gridPosition = selectedNode.gridPosition ?? DEFAULT_GRID_POSITION;

  const handleGridFieldChange = (field: keyof GridPosition, value: string) => {
    if (!onSetGridPosition) return;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return;
    onSetGridPosition(selectedNode.componentId, { ...gridPosition, [field]: parsed });
  };

  return (
    <div className="composer-inspector" data-testid="composer-inspector">
      <div className="composer-inspector__header">
        <span className="composer-inspector__component-id">{selectedNode.componentId}</span>
        <ChipAtom label={selectedNode.componentType} />
      </div>
      <div className="composer-inspector__origin">{originLabel}</div>

      {onSetGridPosition && (
        <div className="composer-inspector__field composer-inspector__grid-position" data-testid="composer-inspector-grid-position">
          <span className="composer-inspector__chip-group-heading">{t('composer.inspector.gridPosition.heading')}</span>
          <div className="composer-inspector__grid-position-inputs">
            {(['x', 'y', 'width', 'height'] as const).map((field) => (
              <label key={field} className="composer-inspector__grid-position-field">
                {t(`composer.inspector.gridPosition.${field}`)}
                <InputAtom
                  type="number"
                  data-testid={`composer-inspector-grid-position-${field}`}
                  value={String(gridPosition[field])}
                  onChange={(e) => handleGridFieldChange(field, e.target.value)}
                />
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="composer-inspector__field composer-inspector__document-lane" data-testid="composer-inspector-document-lane">
        <label htmlFor="composer-inspector-document-context">{t('composer.inspector.document.contextLabel')}</label>
        <DropdownAtom
          inputId="composer-inspector-document-context"
          value={documentContext ?? null}
          options={documentContext ? [{ label: documentContext, value: documentContext }] : []}
          optionLabel="label"
          optionValue="value"
          placeholder={t('composer.inspector.document.unbound')}
          disabled
          onChange={() => undefined}
        />
      </div>

      {emits.length > 0 && (
        <div className="composer-inspector__chip-group" data-testid="composer-inspector-emits">
          <span className="composer-inspector__chip-group-heading">{t('composer.inspector.emitsHeading')}</span>
          {emits.map((emit) => (
            <ChipAtom key={emit} label={emit} />
          ))}
        </div>
      )}

      {compositionInterface && (
        <div className="composer-inspector__interface-panel" data-testid="composer-inspector-interface-panel">
          {propEntries.length > 0 && (
            <div className="composer-inspector__field">
              <span className="composer-inspector__chip-group-heading">{t('composer.inspector.propsHeading')}</span>
              <ul className="composer-inspector__prop-list">
                {propEntries.map(([name, def]) => (
                  <li key={name}>
                    <span className="composer-inspector__prop-name">{name}</span>
                    <span className="composer-inspector__prop-type">{def.type}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {slots.length > 0 && (
            <div className="composer-inspector__chip-group">
              <span className="composer-inspector__chip-group-heading">{t('composer.inspector.slotsHeading')}</span>
              {slots.map((slot) => (
                <ChipAtom key={slot.name} label={slot.name} />
              ))}
            </div>
          )}
        </div>
      )}

      {selectedNode.compositionRef && onOpenInPlayground && (
        <div className="composer-inspector__interface-actions">
          <ButtonAtom
            variant="secondary"
            size="small"
            onClick={() => onOpenInPlayground(selectedNode.compositionRef!.systemName)}
          >
            {t('composer.inspector.openInPlayground')}
          </ButtonAtom>
        </div>
      )}

      {selectedNode.compositionRef && onChangeRebasePolicy && (
        <RebasePolicyControlMolecule
          policy={selectedNode.compositionRef.version !== undefined ? 'pinned' : 'auto'}
          resolvedVersion={rebasePolicyResolvedVersion}
          onChange={(policy, componentVersion) => onChangeRebasePolicy(selectedNode.componentId, policy, componentVersion)}
          data-testid="composer-inspector-rebase-policy"
        />
      )}

      {showOverrideLane && (
        <div className="composer-inspector__override-lane" data-testid="composer-inspector-override-lane">
          <div className="composer-inspector__field">
            <label htmlFor="composer-inspector-override-context">
              {t('composer.inspector.override.contextLabel')}
            </label>
            <DropdownAtom
              inputId="composer-inspector-override-context"
              value={sessionOverride?.contextSystemName ?? null}
              options={contextOptions}
              optionLabel="label"
              optionValue="value"
              showClear
              placeholder={t('composer.inspector.override.contextPlaceholder')}
              disabled={contextOptions.length === 0}
              onChange={(e) => onOverrideContextChange?.(selectedNode.componentId, e.value ?? undefined)}
            />
          </div>
          <div className="composer-inspector__field">
            <label htmlFor="composer-inspector-override-view">{t('composer.inspector.override.viewLabel')}</label>
            <DropdownAtom
              inputId="composer-inspector-override-view"
              value={sessionOverride?.viewSystemName ?? null}
              options={viewOptions}
              optionLabel="label"
              optionValue="value"
              showClear
              placeholder={
                overrideViewsLoading
                  ? t('composer.inspector.override.viewsLoading')
                  : t('composer.inspector.override.viewPlaceholder')
              }
              disabled={!sessionOverride?.contextSystemName || viewOptions.length === 0}
              onChange={(e) => onOverrideViewChange?.(selectedNode.componentId, e.value ?? undefined)}
            />
          </div>
          <div className="composer-inspector__override-actions">
            <ButtonAtom
              variant="secondary"
              size="small"
              disabled={!hasOverride}
              onClick={() => onApplyOverrideToDocument?.(selectedNode.componentId)}
            >
              {t('composer.inspector.override.applyToDocument')}
            </ButtonAtom>
            <ButtonAtom
              variant="text"
              size="small"
              disabled={!hasOverride}
              onClick={() => onClearOverride?.(selectedNode.componentId)}
            >
              {t('composer.inspector.override.clear')}
            </ButtonAtom>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComposerInspectorMolecule;
